import { LocationDescriptor as To } from "history";
import { parse } from "query-string";
import React, { useContext } from "react";
import { ReactReduxContext } from "react-redux";
import { LinkProps, useHistory } from "react-router-dom";

import { loginUrl, signupUrl } from "@/mk2/apps/users/urls";
import { Route } from "@/mk2/containers/Application/Application";
import { hasRoute } from "@/mk2/helpers/router";
import { RouterContext } from "@/mk2/services/RouterContext";
import { HistoryLocationState, RouteNavigateFunction } from "@/mk2/services/browserHistory";
import { apiAuth } from "@/mk2/services/browserXhr";
import {
    createPathBackport,
    parsePathBackport,
    useNavigateBackport,
} from "@/mk2/services/historyBackports";
import { CURRENT_REVISION } from "@/mk/autogenerated/gitData";
import { hasAds } from "@/mk/bazaar/common/userUtils";
import { BASE_URL, IS_PROD } from "@/mk/settings";

export type OwnProps =
    // inherit from react-router-dom/LinkProps
    LinkProps & {
        to: To | null;
        state?: Partial<HistoryLocationState>;
    };

interface InjectedProps {
    routes: Route[];
    routeNavigate: RouteNavigateFunction;
    release?: string;
    requestUserHasAds?: boolean;
}

const baseUrlRe = new RegExp("^" + BASE_URL);

class LinkWithRoutes extends React.PureComponent<OwnProps & InjectedProps> {
    public static defaultProps: Pick<OwnProps & InjectedProps, "replace"> = {
        replace: false,
    };

    public render() {
        const { to, replace, innerRef, routes, requestUserHasAds, ...rest } = this.props;

        let href: string = !to ? "" : typeof to == "string" ? to : createPathBackport(to);

        // https://trello.com/c/tamkDLUC/6540-do-get-parametrov-pre-login-a-signup-linky-v-headeri-nevkladat-nexturl-ale-pridat-az-na-onclick
        // Ak URL obsahuje query string, skontroluj parameter `next`.
        // Ak ide o login alebo signup URL, odstráni sa query časť.
        const hrefStartsWithSignupOrLogin = href.startsWith(loginUrl) || href.startsWith(signupUrl);
        if (href.includes("?")) {
            const qs = parse(href.substr(href.indexOf("?")));
            if (qs.next && hrefStartsWithSignupOrLogin) {
                href = href.startsWith(loginUrl) ? loginUrl : signupUrl;
            }
        }

        // Určí, či sa má zobraziť interstitial reklama od Googlu.
        // 1. Ak URL smeruje na login alebo signup, reklama sa nezobrazí ('false').
        // 2. Inak sa použije hodnota z atribútu `data-google-interstitial`.
        // 3. Ak chýba, použije sa `requestUserHasAds` (či má používateľ povolené reklamy).
        // 4. Ak nič z toho nie je dostupné, výsledok bude 'false'.
        const dataGoogleInterstitial = hrefStartsWithSignupOrLogin
            ? "false"
            : ((rest["data-google-interstitial"] || requestUserHasAds) ?? "false");

        return (
            <a
                {...rest}
                onClick={this.onClick}
                href={href}
                ref={innerRef}
                data-google-interstitial={dataGoogleInterstitial}
            />
        );
    }

    private onClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        const { to, state, onClick, target, replace, routes, routeNavigate, release } = this.props;

        const dest: To = !to
            ? { pathname: "" }
            : typeof to === "string"
              ? parsePathBackport(to)
              : to;
        if (dest.pathname) {
            // Remove BASE_URL from absolute links
            dest.pathname = dest.pathname.replace(baseUrlRe, "");
        }

        if (onClick) {
            onClick(event);
        }

        if (
            !event.defaultPrevented && // onClick prevent default
            event.button === 0 && // ignore everything but left clicks
            !target && // let browser handle "target=_blank" etc.
            !isModifiedEvent(event) && // ignore clicks with modifier keys
            hasRoute(dest.pathname, routes) // Check if we have handler for this URL
        ) {
            event.preventDefault();

            if (IS_PROD && !!release && release !== CURRENT_REVISION) {
                window.location.href = dest.pathname;
            } else {
                try {
                    apiAuth.verifier(); // Throws exception if csrf token has changed
                    routeNavigate(dest, {
                        replace,
                        state: { ...dest.state, ...state },
                    });
                } catch (e) {
                    window.location.href = location.pathname;
                }
            }
        }
    };
}

export const Link: React.FunctionComponent<OwnProps> = React.memo((props) => {
    const { routes } = useContext(RouterContext);
    const history = useHistory<HistoryLocationState>();
    const routeNavigate = useNavigateBackport(history);
    const context = useContext(ReactReduxContext);
    const state = context?.store?.getState();

    const requestUser = state?.request?.user;
    const requestUserHasAds = requestUser ? hasAds(requestUser) : undefined;

    return (
        <LinkWithRoutes
            {...props}
            routes={routes}
            routeNavigate={routeNavigate}
            release={state?.release?.version}
            requestUserHasAds={requestUserHasAds}
        />
    );
});

Link.displayName = "Link";

function isModifiedEvent(event: React.MouseEvent<HTMLAnchorElement>) {
    return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}
